<!-- @component `Hero` The introduction to the story. -->
<script lang="ts">
	// IMPORTS
	import { assets } from '$app/paths';
	import { mutedStore, toggleMuted } from '$stores/mutedStore';
	import { gsap } from '$utils/gsap.svelte.js';
	import { ScrollTrigger } from '$utils/gsap.svelte.js';
	import { autoScrollStore, toggleAutoScroll } from '$stores/autoScrollStore';

	// PROPS
	export let title: string;
	export let subtitle: string;

	// VARIABLES
	$: muted = $mutedStore.muted;

	// LOGIC
	function fadeHeroBg(el: HTMLElement) {
		gsap.to(el, {
			backgroundColor: '#202223',
			scrollTrigger: {
				trigger: el,
				start: '50% 20%',
				end: '60% top',
				scrub: 0.5,
			},
		});
	}

	function handleClick() {
		toggleMuted();
		toggleAutoScroll();
	}
</script>

<header class="hero" use:fadeHeroBg>
	<div class="hero-top">
		<div class="hero-image">
			<img src="{`${assets}/images/person.svg`}" alt="Hero image" />
		</div>
	</div>
	<div class="hero-bottom">
		<div class="hero-text">
			<h1>{title}</h1>
			<p class="subtitle">{subtitle}</p>
		</div>
		<div class="button-container">
			{#if muted}
				<button class="audio audio-off" on:click="{handleClick}"
					><svg
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
						class="lucide lucide-headphones"
						><path
							d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3"
						></path></svg
					>Turn on audio and autoscroll</button
				>
				<p>
					Turn this on to experience the story with audio narration and
					automatically scroll the diary entries as they are played.
				</p>
			{:else}
				<button class="audio audio-on" on:click="{handleClick}"
					><svg
						xmlns="http://www.w3.org/2000/svg"
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
						class="lucide lucide-volume-2"
						><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path
							d="M15.54 8.46a5 5 0 0 1 0 7.07"
						></path><path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path></svg
					> Audio On</button
				>
				<p>
					Audio and autoscroll are <strong>on</strong>. <br />Change these
					settings anytime in the
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="18"
						height="18"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
						class="lucide lucide-menu"
						><line x1="4" x2="20" y1="12" y2="12"></line><line
							x1="4"
							x2="20"
							y1="6"
							y2="6"
						></line><line x1="4" x2="20" y1="18" y2="18"></line></svg
					> menu.
				</p>
			{/if}
		</div>
	</div>
</header>

<style lang="scss">
	@import '$styles/colors';
	@import '$styles/mq';

	header {
		display: flex;
		flex-direction: column;
		background-color: var(--theme-accent);
		width: 100%;
		height: 100svh;

		@include sizes(medium) {
			padding: 0;
		}
	}

	.hero-top {
		display: flex;
		flex-grow: 1;
		flex-direction: column;
		border-bottom: 2px solid var(--theme-primary);
		height: 50svh;

		img {
			display: block;
			margin: 0 auto -2px auto;
			padding-top: 20px;
			max-width: 700px;
			max-height: 50svh;
		}
	}

	.hero-bottom {
		display: flex;
		flex-shrink: 0;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		background: var(--theme-primary);
		height: 50svh;
	}

	.hero-text {
		margin-bottom: 32px;
	}

	h1,
	p {
		color: var(--theme-secondary);
		font-family: var(--brand-font-ui-cn);
		text-align: center;
	}

	h1 {
		margin-bottom: 8px;
		font-weight: 900;
		font-size: 2.8rem;
		line-height: 1;
		text-transform: uppercase;
	}

	p.subtitle {
		color: $gray-3;
		font-weight: 500;
		font-size: 1.4rem;
	}

	.button-container {
		font-family: var(--brand-font-ui-cn);
	}

	.button-container p {
		display: none;
		margin: 0 auto;
		max-width: 400px;
		color: $gray-6;
		font-family: var(--brand-font-ui-cn);

		@include sizes(medium) {
			display: block;
		}

		svg {
			display: inline;
			vertical-align: -4px;
		}
	}

	button {
		display: block;
		margin-right: auto;
		margin-bottom: 32px;
		margin-left: auto;
		box-shadow: var(--shadow-elevation-medium);
		border: 1px solid var(--theme-secondary);
		border-radius: 30px;
		background: var(--theme-secondary);
		padding: 8px 12px 7px 12px;
		color: var(--theme-primary);
		font-weight: 700;
		font-size: 0.8rem;
		text-transform: uppercase;

		@include sizes(medium) {
			font-size: 1rem;
		}

		&.audio-on {
			border: 1px solid $green-6;
			background: $green-5;
			color: var(--theme-secondary);

			&:hover {
				background: $green-6;
			}
		}

		svg {
			display: inline-block;
			vertical-align: -5px;
			margin-right: 5px;
			width: 18px;
			height: 18px;
		}

		&:hover {
			border: 1px solid transparent;
			background: var(--theme-accent);
			color: var(--theme-secondary);
		}
	}
</style>
